import React, { useState } from "react"
import useFormField from "../../../../hooks/useFormField"
import FormInputLabel from "../labels/form-field-label"
import FormFieldError from "../labels/form-field-error"
import ToggleInfo from "../buttons/toggle-info-button"

export default function FileUploadInput({
  name,
  id,
  label,
  description,
  required,
  fileTypes,
  allowMultiple,
  validateField,
  globalValue,
  displayOption,
  setGlobalValue,
  error,
  setError,
  fileUploadPlaceholder,
  fileUploadHelperText,
  fileUploadTooManyFilesError,
  closeModalLabel,
  moreInfoLabel,
}) {
  const { handleFocus, isFocused, handleBlurFiles, handleFileChange } = useFormField(
    globalValue,
    validateField,
    globalValue,
    setGlobalValue,
    setError,
  )

  const [dragActive, setDragActive] = useState(false)

  const handleDragEnter = (e) => {
    e.preventDefault()
    e.stopPropagation()
    setDragActive(true)
  }

  const handleDragOver = (e) => {
    e.preventDefault()
    e.stopPropagation()
    setDragActive(true)
  }

  const handleDragLeave = (e) => {
    e.preventDefault()
    e.stopPropagation()
    if (e.target === e.currentTarget) {
      setDragActive(false)
    }
  }

  const handleDrop = (e) => {
    e.preventDefault()
    e.stopPropagation()
    setDragActive(false)

    const files = e.dataTransfer.files

    if (files && files.length) {
      if (!allowMultiple && files.length > 1) {
        setError(fileUploadTooManyFilesError || "Du må kun uploade én fil i dette felt.")
        return
      }

      setError(null)
      handleFileChange(files)
    }
  }

  const handleFileRemove = () => {
    setGlobalValue(null)
    handleBlurFiles(null)
  }

  return (
    <div
      className={`form-element form-element--file Form__Element ${displayOption} ${isFocused ? "has-focus" : ""} ${error ? "is-invalid" : ""}`}
      data-f-element-name={name}
      data-f-type="fileupload"
      onDragEnter={handleDragEnter}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      <div className="form-labels">
        <FormInputLabel id={id} label={label} required={required} />
        <ToggleInfo description={description} id={id} closeModalLabel={closeModalLabel} moreInfoLabel={moreInfoLabel} />
      </div>

      {!globalValue ? (
        <div className={`file-upload__placeholder ${dragActive ? "drag-active" : ""}`}>
          <label htmlFor={id} className="file-upload__label">
            <span className="file-upload__text">{fileUploadPlaceholder}</span>
            <span className="file-upload__icon">
              <svg className="svg-icon svg-icon--PlusIcon">
                <use xlinkHref="/dist/svg/symbols.svg#svg-PlusIcon"></use>
              </svg>
            </span>
          </label>
          <input
            className="form-input visually-hidden"
            type="file"
            name={name}
            id={id}
            onChange={(e) => handleFileChange(e.target.files)}
            accept={fileTypes}
            multiple={allowMultiple}
            data-f-datainput=""
            aria-describedby={`${name}_desc`}
            required={required}
            aria-required={required}
            title={description}
            aria-label={description + (fileUploadPlaceholder != null ? " " + fileUploadPlaceholder : "")}
            onFocus={handleFocus}
            onBlur={(e) => handleBlurFiles(e.target.files)}
          />
        </div>
      ) : (
        <div className="file-upload__selected">
          <span className="file-upload__file-icon">
            <svg className="svg-icon svg-icon--paperclip">
              <use xlinkHref="/dist/svg/symbols.svg#svg-paperclip"></use>
            </svg>
          </span>
          <span className="file-upload__file-name">
            {globalValue &&
              Array.from(globalValue)
                .map((file) => file.name)
                .join(", ")}
          </span>
          <button
            tabIndex={0}
            type="button"
            className="file-upload__delete"
            onClick={handleFileRemove}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault()
                handleFileRemove()
              }
            }}
            aria-label="Remove uploaded file"
            onFocus={handleFocus}
            onBlur={() => handleBlurFiles(globalValue)}
          >
            <svg className="svg-icon svg-icon--trashcan">
              <use xlinkHref="/dist/svg/symbols.svg#svg-trashcan"></use>
            </svg>
          </button>
        </div>
      )}

      <FormFieldError name={name} error={error} />
      <div className="file-upload__help-text">{fileUploadHelperText}</div>
    </div>
  )
}
